/**
 * Newsletters Panel — Concierge® Newsletter Generator (Spec §1).
 *
 * Lives inside the per-client Customer Intelligence dashboard as a tab.
 * Shows the list of newsletters for the current client, plus an
 * "Open editor" button that drills into the inline editor + live preview.
 *
 * Phase 1+2: Mode A (paste content) + inline edit + section reorder + approve.
 * Phase 3 (push to Zoho) + Phase 4 (Claude auto-draft + cron) come next.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { copyNewsletterHtml, downloadNewsletterHtml } from '@/lib/newsletterHtml';
import { Mail, Plus, Eye, Edit3, CheckCircle2, Clock, FileText, Sparkles, Code, Download } from 'lucide-react';
import NewsletterAutoDraftModal from './NewsletterAutoDraftModal';
import NewsletterCheatGuide from './NewsletterCheatGuide';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const STATUS_PILL = {
  draft:          { bg: '#FEF3C7', fg: '#92400E', label: 'Draft' },
  ready_to_send:  { bg: '#DCFCE7', fg: '#166534', label: 'Ready to Send' },
  sent:           { bg: '#DBEAFE', fg: '#1E3A8A', label: 'Sent' },
};

export default function NewslettersPanel({ clientId, canEdit }) {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAutoDraft, setShowAutoDraft] = useState(false);
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newHeadline, setNewHeadline] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/newsletters/list?client_id=${encodeURIComponent(clientId)}`);
      setNewsletters(r.data?.newsletters || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const seedMayToNov = async () => {
    if (!canEdit) return;
    if (!confirm(
      'Seed all 7 JLR newsletters (May → Nov 2026)?\n\n'
      + '• Inserts text + structure immediately\n'
      + '• Triggers Tavily image auto-fill in the background (~2-5 min)\n'
      + '• Idempotent — re-running refreshes content but preserves images you already approved'
    )) return;
    setSeedBusy(true);
    try {
      const r = await api.post('/newsletters/_admin/seed-may-nov-2026-jlr');
      await load();
      alert(
        `Seeded ${r.data.seeded} newsletters (May–Nov 2026).\n\n`
        + `Images are auto-filling in the background — refresh in ~3 minutes to see them populated.`
      );
    } catch (e) {
      alert(`Seed failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeedBusy(false);
    }
  };

  const createNew = async () => {
    if (!newHeadline.trim()) {
      alert('Add a headline first (e.g. "May with Concierge®")');
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/newsletters/create', {
        client_id: clientId,
        month: Number(newMonth),
        year: Number(newYear),
        headline: newHeadline.trim(),
        sections: [],
      });
      setShowCreate(false); setNewHeadline('');
      await load();
      navigate(`/newsletters/${r.data.id}`);
    } catch (e) {
      alert(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ci-newsletters-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#1B2A4A' }}>
            <Mail size={16} style={{ color: '#7A5C2B' }} /> Newsletters
          </h2>
          <p className="text-[12px] mt-0.5" style={{ color: '#6B7280' }}>
            Concierge® monthly newsletter — paste, edit, approve, push to Zoho.
            {' '}Personalises per member from Soul Form data.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {clientId === 'jlr' && (
              <button
                onClick={seedMayToNov} disabled={seedBusy}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
                style={{ borderColor: '#7A5C2B', color: '#7A5C2B' }}
                data-testid="newsletter-seed-may-nov"
                title="Seed May–Nov 2026 newsletters from the JLR Word doc. Idempotent — preserves existing images.">
                <FileText size={12} /> {seedBusy ? 'Seeding 7 months…' : 'Seed May–Nov 2026 (7 months)'}
              </button>
            )}
            <button
              onClick={() => setShowAutoDraft(true)}
              className="px-3 py-1.5 rounded-md text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
              style={{ borderColor: '#7A5C2B', color: '#7A5C2B' }}
              data-testid="newsletter-autodraft-btn">
              <Sparkles size={12} /> Auto-draft with Claude
            </button>
            <button
              onClick={() => setShowCreate(s => !s)}
              className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white inline-flex items-center gap-1"
              style={{ backgroundColor: '#1C304A' }}
              data-testid="newsletter-new-btn">
              <Plus size={12} /> New newsletter
            </button>
          </div>
        )}
      </div>

      <NewsletterCheatGuide />

      {showCreate && (
        <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="newsletter-create-form">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Month</label>
              <select
                value={newMonth} onChange={(e) => setNewMonth(e.target.value)}
                data-testid="newsletter-create-month"
                className="w-full mt-1 px-2 py-1.5 border rounded text-[13px]" style={{ borderColor: '#E5E1DC' }}>
                {MONTH_NAMES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Year</label>
              <input
                type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)}
                data-testid="newsletter-create-year"
                className="w-full mt-1 px-2 py-1.5 border rounded text-[13px]" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Client</label>
              <input value={clientId} disabled
                className="w-full mt-1 px-2 py-1.5 border rounded text-[13px] bg-gray-50" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Headline</label>
            <input
              value={newHeadline} onChange={(e) => setNewHeadline(e.target.value)}
              data-testid="newsletter-create-headline"
              placeholder="e.g. May with Concierge®"
              className="w-full mt-1 px-2 py-1.5 border rounded text-[13px]" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 rounded-md text-[12px] border hover:bg-gray-50"
              style={{ borderColor: '#E5E1DC' }}>
              Cancel
            </button>
            <button
              onClick={createNew} disabled={creating}
              data-testid="newsletter-create-submit"
              className="px-3 py-1.5 rounded-md text-[12px] text-white"
              style={{ backgroundColor: '#1C304A', opacity: creating ? 0.6 : 1 }}>
              {creating ? 'Creating…' : 'Create + open editor'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-[12px] px-3 py-2 rounded-md" style={{ background: '#FEE2E2', color: '#7a1f1f' }}>{error}</div>
      )}

      {loading ? (
        <div className="text-[12px]" style={{ color: '#9CA3AF' }}>Loading…</div>
      ) : newsletters.length === 0 ? (
        <div className="text-[12px] p-6 text-center rounded-md" style={{ background: '#F7F5F2', color: '#6B7280' }}>
          No newsletters yet. {canEdit ? 'Create one above, or seed the May 2026 example.' : 'Ask the CEO to set up the first newsletter.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-[13px]" data-testid="newsletter-list-table">
            <thead>
              <tr style={{ background: '#F7F5F2', color: '#6B7280' }}>
                <th className="text-left px-3 py-2 font-medium">Month</th>
                <th className="text-left px-3 py-2 font-medium">Subject</th>
                <th className="text-left px-3 py-2 font-medium">Sections</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Updated</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {newsletters.map(n => {
                const pill = STATUS_PILL[n.status] || STATUS_PILL.draft;
                return (
                  <tr key={n.id} className="border-t hover:bg-gray-50" style={{ borderColor: '#F0EDE7' }}>
                    <td className="px-3 py-2.5">
                      <strong>{MONTH_NAMES[n.month - 1] || n.month}</strong> {n.year}
                    </td>
                    <td className="px-3 py-2.5">{n.subject || '—'}</td>
                    <td className="px-3 py-2.5">{n.section_count} sections · {n.item_count} items</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                        style={{ background: pill.bg, color: pill.fg }}>
                        {pill.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[12px]" style={{ color: '#6B7280' }}>
                      {n.updated_at ? new Date(n.updated_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      }) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => navigate(`/newsletters/${n.id}`)}
                          className="px-2 py-1 rounded text-[11px] border hover:bg-gray-50 inline-flex items-center gap-1"
                          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                          data-testid={`newsletter-edit-${n.id}`}>
                          <Edit3 size={11} /> Edit
                        </button>
                        <button
                          onClick={() => copyNewsletterHtml(n.id)}
                          title="Copy bulk-send HTML to clipboard"
                          className="px-2 py-1 rounded text-[11px] border hover:bg-gray-50 inline-flex items-center gap-1"
                          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                          data-testid={`newsletter-copy-html-${n.id}`}>
                          <Code size={11} /> Copy HTML
                        </button>
                        <button
                          onClick={() => downloadNewsletterHtml(
                            n.id,
                            `${clientId}-${n.year}-${String(n.month).padStart(2, '0')}-newsletter.html`,
                          )}
                          title="Download bulk-send HTML file"
                          className="px-2 py-1 rounded text-[11px] border hover:bg-gray-50 inline-flex items-center gap-1"
                          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                          data-testid={`newsletter-download-html-${n.id}`}>
                          <Download size={11} /> .html
                        </button>
                        <a
                          href={`/c/${n.public_token}`} target="_blank" rel="noreferrer"
                          className="px-2 py-1 rounded text-[11px] border hover:bg-gray-50 inline-flex items-center gap-1"
                          style={{ borderColor: '#E5E1DC', color: '#7A5C2B' }}
                          data-testid={`newsletter-public-${n.id}`}>
                          <Eye size={11} /> Public
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAutoDraft && (
        <NewsletterAutoDraftModal
          clientId={clientId}
          onClose={() => { setShowAutoDraft(false); load(); }}
        />
      )}
    </div>
  );
}
